import { ago } from "../time";
import type { Email, EmailThread, Folder } from "../types/email";
import { hash, int, pick, rng } from "./random";

/**
 * The mailboxes. Like the chats, the threads the screenshots use are
 * hand-written and the rest of each list is generated, so a mailbox looks
 * worked rather than empty.
 */

const MAIL_ACCOUNTS = ["acc_gm_tom", "acc_ol_dan", "acc_im_ops"] as const;

export const FOLDERS: Folder[] = MAIL_ACCOUNTS.flatMap((accountId) => {
  const r = rng(hash(`folders:${accountId}`));
  const base: { name: string; role: Folder["role"] }[] = [
    { name: "Inbox", role: "inbox" },
    { name: "Sent", role: "sent" },
    { name: "Drafts", role: "drafts" },
    { name: "Archive", role: "archive" },
    { name: "Spam", role: "spam" },
    { name: "Trash", role: "trash" },
  ];
  const labels =
    accountId === "acc_gm_tom"
      ? ["Pipeline", "Customers", "Partners"]
      : accountId === "acc_ol_dan"
        ? ["Renewals", "Procurement"]
        : ["Alerts"];

  return [
    ...base.map(({ name, role }) => ({
      id: `fld_${accountId}_${role}`,
      accountId,
      name,
      role,
      unread: role === "inbox" ? int(r, 2, 24) : 0,
      total: int(r, 40, 2200),
    })),
    ...labels.map((name) => ({
      id: `fld_${accountId}_${name.toLowerCase()}`,
      accountId,
      name,
      unread: int(r, 0, 5),
      total: int(r, 12, 240),
    })),
  ];
});

const INBOX = (accountId: string) => `fld_${accountId}_inbox`;

/** The hand-written threads, newest first within each account. */
export const EMAILS: Email[] = [
  {
    id: "eml_rfp_1",
    accountId: "acc_gm_tom",
    provider: "gmail",
    threadId: "thr_rfp",
    subject: "Re: Northwind — security questionnaire + DPA",
    body: `Tom,

Legal cleared the DPA this morning. Two items left from security:

1. Where is message content stored, and for how long?
2. Can we scope the API key to read-only for the pilot?

If both are a yes we can sign this week.

Best,
Devika Menon
VP Revenue Operations, Lumen Freight`,
    from: { name: "Devika Menon", address: "devika@lumenfreight.com" },
    to: [{ name: "Tom Okafor", address: "tom@northwind.io" }],
    cc: [{ name: "Legal", address: "legal@lumenfreight.com" }],
    bcc: [],
    at: ago(42, "m"),
    direction: "inbound",
    read: false,
    starred: true,
    folderIds: [INBOX("acc_gm_tom"), "fld_acc_gm_tom_pipeline"],
    attachments: [
      {
        id: "atch_dpa",
        kind: "file",
        name: "Lumen-Northwind-DPA-signed.pdf",
        size: 311_448,
        mimeType: "application/pdf",
      },
    ],
  },
  {
    id: "eml_rfp_0",
    accountId: "acc_gm_tom",
    provider: "gmail",
    threadId: "thr_rfp",
    subject: "Northwind — security questionnaire + DPA",
    body: `Hi Devika,

Attaching our completed questionnaire and the DPA for your legal team.

Short version: message content is encrypted at rest, retained for 30 days by
default, and configurable down to zero. API keys can be scoped per
environment and per capability.

Happy to walk your security lead through it on a call.

Tom`,
    from: { name: "Tom Okafor", address: "tom@northwind.io" },
    to: [{ name: "Devika Menon", address: "devika@lumenfreight.com" }],
    cc: [],
    bcc: [],
    at: ago(3, "d"),
    direction: "outbound",
    read: true,
    starred: false,
    folderIds: ["fld_acc_gm_tom_sent"],
    attachments: [
      {
        id: "atch_q",
        kind: "file",
        name: "northwind-security-questionnaire.xlsx",
        size: 88_204,
        mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
    ],
    openedAt: ago(2, "d"),
  },
  {
    id: "eml_renew_1",
    accountId: "acc_ol_dan",
    provider: "outlook",
    threadId: "thr_renew",
    subject: "Keystone renewal — 14 Nov",
    body: `Dan,

Our Keystone contract is up on 14 November. Finance wants the uplift under 8%
and procurement wants the two unused seats removed.

Can you put together the renewal pack before Friday?

Thanks,
Aisha`,
    from: { name: "Aisha Kone", address: "aisha@northwind.io" },
    to: [{ name: "Dan Whitfield", address: "dan@northwind.io" }],
    cc: [],
    bcc: [],
    at: ago(4, "h"),
    direction: "inbound",
    read: false,
    starred: false,
    folderIds: [INBOX("acc_ol_dan"), "fld_acc_ol_dan_renewals"],
    attachments: [],
  },
  {
    id: "eml_alert_1",
    accountId: "acc_im_ops",
    provider: "imap",
    threadId: "thr_alert",
    subject: "[Aux0] Account needs attention: Tom Okafor (iCloud)",
    body: `Apple revoked the app password for tom.okafor@icloud.com when the
Apple Account password changed.

Account: acc_ic_tom
Since:   3 hours ago
Fix:     send a hosted reconnect link

This is a self-serve fix; the user generates a new app password and the
mailbox reconnects in under a minute.`,
    from: { name: "Aux0", address: "alerts@aux0.com" },
    to: [{ name: "Northwind Ops", address: "ops@northwind.io" }],
    cc: [],
    bcc: [],
    at: ago(3, "h"),
    direction: "inbound",
    read: false,
    starred: false,
    folderIds: [INBOX("acc_im_ops"), "fld_acc_im_ops_alerts"],
    attachments: [],
  },
  {
    id: "eml_draft_1",
    accountId: "acc_gm_tom",
    provider: "gmail",
    threadId: "thr_rfp",
    subject: "Re: Northwind — security questionnaire + DPA",
    body: `Devika,

Both are a yes.

1. Content is encrypted at rest in eu-west-1 and retained 30 days by default.
   We can set your project to zero retention, in which case Aux0 holds only
   message ids and timestamps.
2. Read-only keys are a capability scope on the key itself — I'll create one
   for the pilot and send it through your vault.

Shall I book 20 minutes with your security lead on Thursday?`,
    from: { name: "Tom Okafor", address: "tom@northwind.io" },
    to: [{ name: "Devika Menon", address: "devika@lumenfreight.com" }],
    cc: [],
    bcc: [],
    at: ago(6, "m"),
    direction: "outbound",
    read: true,
    starred: false,
    folderIds: ["fld_acc_gm_tom_drafts"],
    attachments: [],
    isDraft: true,
  },
];

const FILLER_SUBJECTS = [
  "Q4 pipeline review — agenda",
  "Invoice 2026-1184",
  "Re: intro to the platform team",
  "Your weekly usage summary",
  "Contract redlines attached",
  "Re: availability next week",
  "Partner program — next steps",
  "Onboarding checklist for Brightpath",
  "Re: API rate limits",
  "Follow-up: pricing for 50 seats",
  "Security review scheduled",
  "Re: data residency question",
];

const FILLER_SENDERS = [
  { name: "Hiro Tanaka", address: "hiro@pallas.dev" },
  { name: "Claudia Moreau", address: "claudia@vela.fr" },
  { name: "Sam Okonkwo", address: "sam@keystone.co" },
  { name: "Elena Petrova", address: "elena@sablehq.com" },
  { name: "Grace Lin", address: "grace@lumenfreight.com" },
  { name: "Marco Rossi", address: "marco@stadler.de" },
  { name: "Billing", address: "billing@stripe.com" },
  { name: "GitHub", address: "noreply@github.com" },
];

/** Thread rows beyond the authored ones, for each mailbox's list. */
function fillerThreads(accountId: string, count: number): EmailThread[] {
  const r = rng(hash(`threads:${accountId}`));
  return Array.from({ length: count }, (_, index) => {
    const sender = pick(r, FILLER_SENDERS);
    const minutesAgo = int(r, 70, 60 * 24 * 30);
    const unread = r() < 0.24 ? int(r, 1, 2) : 0;
    return {
      id: `thr_${accountId}_${index}`,
      accountId,
      subject: pick(r, FILLER_SUBJECTS),
      participants: [sender],
      messageCount: int(r, 1, 6),
      unread,
      lastMessageAt: ago(minutesAgo, "m"),
      snippet: pick(r, [
        "Thanks — I've added my notes inline and copied Priya.",
        "Can we push this to Monday? I'm out tomorrow.",
        "Looks right. One correction on the second table.",
        "Approved on our side. Over to procurement.",
        "No action needed, just closing the loop.",
      ]),
      folderIds: [INBOX(accountId)],
      hasAttachments: r() < 0.3,
    } satisfies EmailThread;
  });
}

/** The authored threads, assembled from EMAILS, then the filler. */
export const THREADS: EmailThread[] = [
  ...["thr_rfp", "thr_renew", "thr_alert"].map((threadId) => {
    const messages = EMAILS.filter(
      (email) => email.threadId === threadId && !email.isDraft,
    ).sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
    const newest = messages[0];
    if (!newest) throw new Error(`Thread ${threadId} has no messages.`);
    const participants = [
      ...new Map(
        messages
          .flatMap((email) => [email.from, ...email.to])
          .map((a) => [a.address, a]),
      ).values(),
    ];
    return {
      id: threadId,
      accountId: newest.accountId,
      subject: newest.subject.replace(/^Re:\s*/, ""),
      participants,
      messageCount: messages.length,
      unread: messages.filter((email) => !email.read).length,
      lastMessageAt: newest.at,
      snippet:
        newest.body
          .split("\n")
          .find((line) => line.trim())
          ?.trim() ?? "",
      folderIds: newest.folderIds,
      hasAttachments: messages.some((email) => email.attachments.length > 0),
    } satisfies EmailThread;
  }),
  ...fillerThreads("acc_gm_tom", 22),
  ...fillerThreads("acc_ol_dan", 14),
  ...fillerThreads("acc_im_ops", 8),
];

export const THREAD_BY_ID = new Map(THREADS.map((thread) => [thread.id, thread]));
